import React, { useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeContext } from "../../../../context/ThemeContext";

// Icons
const BackIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>);
const CloseIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>);
const LocationIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="12" r="3"/></svg>);
const PhoneIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.574 2.81.7A2 2 0 0 1 22 16.92z"/></svg>);
const CalendarIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>);
const CreditCardIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>);
const PackageIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>);
const StoreIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>);
const UserIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>);
const MoneyIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>);
const TruckIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="6" width="18" height="12" rx="2"/><circle cx="6" cy="18" r="2"/><circle cx="18" cy="18" r="2"/></svg>);
const CheckCircleIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>);
const ClockIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>);
const CircleIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="8"/></svg>);

// ✅ Format currency - Always show 2 decimal places
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "₹0.00";
  const num = parseFloat(amount);
  if (isNaN(num)) return "₹0.00";
  return `₹${num.toFixed(2)}`;
};

const StatusBadge = ({ status, theme }) => {
  const getConfig = () => {
    switch(status?.toLowerCase()) {
      case "completed": return { bg: `${theme.success}26`, color: theme.success };
      case "pending": return { bg: `${theme.warning}26`, color: theme.warning };
      case "cancelled": return { bg: `${theme.error || theme.danger}26`, color: theme.error || theme.danger };
      case "accepted": return { bg: `${theme.primary}26`, color: theme.primary };
      case "preparing": return { bg: "#a855f726", color: "#a855f7" };
      case "ready": return { bg: "#ec489926", color: "#ec4899" };
      case "out_for_delivery": return { bg: "#06b6d426", color: "#06b6d4" };
      case "delivered": return { bg: `${theme.success}26`, color: theme.success };
      case "dropped": return { bg: "#6b728026", color: "#6b7280" };
      default: return { bg: `${theme.textMuted}1f`, color: theme.textMuted };
    }
  };
  const c = getConfig();
  return (
    <span style={{
      display: "inline-block", padding: "4px 12px", borderRadius: "20px",
      fontSize: "12px", fontWeight: 600, background: c.bg, color: c.color,
    }}>{status}</span>
  );
};

// Redesigned Vertical Timeline Event Component (Theme-Aware)
const VerticalTimelineEvent = ({ label, value, isCompleted, isLast, theme, suffix = "" }) => {
  return (
    <div style={{ display: "flex", position: "relative", marginBottom: isLast ? 0 : "24px" }}>
      {/* Timeline Line & Icon */}
      <div style={{ position: "relative", width: "60px", flexShrink: 0 }}>
        {/* Vertical connecting line */}
        {!isLast && (
          <div style={{
            position: "absolute",
            top: "32px",
            left: "28px",
            width: "2px",
            height: "calc(100% + 8px)",
            background: isCompleted ? theme.success : `${theme.border}`,
            zIndex: 0
          }} />
        )}
        
        {/* Icon Circle */}
        <div style={{
          width: "40px",
          height: "40px",
          borderRadius: "50%",
          background: isCompleted ? theme.success : theme.surfaceLighter,
          border: isCompleted ? `2px solid ${theme.success}` : `2px solid ${theme.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          zIndex: 1,
          boxShadow: isCompleted ? `0 0 0 4px ${theme.success}20` : "none"
        }}>
          {isCompleted ? (
            <CheckCircleIcon style={{ width: "20px", height: "20px", color: "#fff" }} />
          ) : (
            <CircleIcon style={{ width: "12px", height: "12px", color: theme.textMuted }} />
          )}
        </div>
      </div>
      
      {/* Content */}
      <div style={{ flex: 1, paddingLeft: "16px", paddingBottom: "8px" }}>
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          flexWrap: "wrap", 
          gap: "8px",
          marginBottom: "6px"
        }}>
          <span style={{ 
            fontSize: "14px", 
            fontWeight: 600, 
            color: isCompleted ? theme.success : theme.textPrimary 
          }}>
            {label}
          </span>
          {isCompleted && (
            <span style={{
              fontSize: "10px",
              padding: "2px 8px",
              borderRadius: "12px",
              background: `${theme.success}20`,
              color: theme.success,
              fontWeight: 500
            }}>
              Completed
            </span>
          )}
          {!isCompleted && value === "—" && (
            <span style={{
              fontSize: "10px",
              padding: "2px 8px",
              borderRadius: "12px",
              background: `${theme.warning}20`,
              color: theme.warning,
              fontWeight: 500
            }}>
              Pending
            </span>
          )}
        </div>
        
        {value && value !== "—" ? (
          <>
            <div style={{ 
              fontSize: "13px", 
              fontWeight: 500, 
              color: theme.textPrimary,
              fontFamily: "'Monaco', monospace",
              marginBottom: "4px"
            }}>
              {value}
            </div>
            {suffix && (
              <div style={{ 
                fontSize: "11px", 
                color: theme.textMuted,
                display: "flex",
                alignItems: "center",
                gap: "4px",
                marginTop: "2px"
              }}>
                <ClockIcon style={{ width: "10px", height: "10px" }} />
                {suffix}
              </div>
            )}
          </>
        ) : (
          <div style={{ 
            fontSize: "12px", 
            color: theme.textMuted,
            fontStyle: "italic"
          }}>
            Waiting for update...
          </div>
        )}
      </div>
    </div>
  );
};

const OrderDetailPage = () => {
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();
  const order = location.state?.order;

  // Parse items from JSON string
  const parseItems = (itemsStr) => {
    try {
      if (typeof itemsStr === 'string') {
        return JSON.parse(itemsStr);
      }
      return itemsStr || [];
    } catch {
      return [];
    }
  };

  const items = parseItems(order?.items);
  
  // Calculate totals with 2 decimal places
  const itemTotal = parseFloat(items.reduce((sum, item) => sum + (item.price * (item.qty || 1)), 0).toFixed(2));
  const finalAmount = parseFloat(parseFloat(order?.final_amount) || parseFloat(order?.price) || 0).toFixed(2);
  const discount = parseFloat(parseFloat(order?.discount) || 0).toFixed(2);
  const tax = parseFloat(parseFloat(order?.tax) || 0).toFixed(2);
  const deliveryCharge = parseFloat(parseFloat(order?.delivery_charge) || 0).toFixed(2);
  const handlingCharge = parseFloat(parseFloat(order?.handling_charge) || 0).toFixed(2);
  const platformFee = parseFloat(parseFloat(order?.user_platform_fee) || parseFloat(order?.business_platform_fee) || 0).toFixed(2);
  const driverAmount = parseFloat(parseFloat(order?.driver_amount) || 0).toFixed(2);
  const businessAmount = parseFloat(parseFloat(order?.business_amount) || 0).toFixed(2);
  const platformAmount = parseFloat(parseFloat(order?.platform_amount) || 0).toFixed(2);

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString || dateString === "null" || dateString === null) return null;
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch {
      return dateString;
    }
  };

  // Get cancel reason based on who cancelled
  const getCancelReason = () => {
    if (order?.user_cancel_time && order?.user_cancel_remarks) {
      return { type: "User Cancel", time: order.user_cancel_time, reason: order.user_cancel_remarks };
    }
    if (order?.business_cancel_time && order?.business_cancel_remarks) {
      return { type: "Business Cancel", time: order.business_cancel_time, reason: order.business_cancel_remarks };
    }
    if (order?.driver_cancel_time && order?.driver_cancel_remarks) {
      return { type: "Driver Cancel", time: order.driver_cancel_time, reason: order.driver_cancel_remarks };
    }
    return null;
  };

  const cancelInfo = getCancelReason();

  // Prepare timeline events array
  const timelineEvents = [
    { 
      label: "Order Placed", 
      value: formatDate(order?.created_at), 
      isCompleted: !!order?.created_at,
      suffix: ""
    },
    { 
      label: "Restaurant Accepted", 
      value: formatDate(order?.restro_accept_time), 
      isCompleted: !!order?.restro_accept_time,
      suffix: order?.restro_prep_duration ? `Prep Duration: ${order.restro_prep_duration}` : ""
    },
    { 
      label: "Restaurant Ready", 
      value: formatDate(order?.restro_ready_time), 
      isCompleted: !!order?.restro_ready_time,
      suffix: ""
    },
    { 
      label: "Rider Pickup", 
      value: formatDate(order?.rider_pickup_time), 
      isCompleted: !!order?.rider_pickup_time,
      suffix: ""
    },
    { 
      label: "Order Delivered", 
      value: formatDate(order?.order_delivered_time), 
      isCompleted: !!order?.order_delivered_time,
      suffix: ""
    }
  ];

  // Add cancel event if exists
  if (cancelInfo) {
    timelineEvents.push({
      label: cancelInfo.type,
      value: formatDate(cancelInfo.time),
      isCompleted: true,
      suffix: cancelInfo.reason
    });
  }

  // ✅ Unified Card Styles - All cards now have the same theme
  const styles = {
    container: {
      minHeight: "100vh",
      background: theme.background,
      padding: "24px",
      fontFamily: "'DM Sans', 'Inter', sans-serif",
      transition: "background 0.3s ease",
    },
    backButton: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      background: "none",
      border: "none",
      color: theme.primary,
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: 500,
      marginBottom: "24px",
      padding: "8px 0",
    },
    header: {
      marginBottom: "24px",
    },
    title: {
      fontSize: "28px",
      fontWeight: 700,
      color: theme.textPrimary,
      margin: 0,
    },
    orderId: {
      fontSize: "14px",
      color: theme.textMuted,
      marginTop: "4px",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
      gap: "16px",
      alignContent: "start",
    },
    // ✅ Unified Card Style
    card: {
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: "16px",
      overflow: "hidden",
      boxShadow: `0 4px 12px ${theme.primary}08`,
      height: "fit-content",
    },
    // ✅ Unified Card Header Style
    cardHeader: {
      padding: "16px 20px",
      borderBottom: `1px solid ${theme.border}`,
      background: theme.surfaceLighter,
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    cardTitle: {
      fontSize: "16px",
      fontWeight: 600,
      color: theme.textPrimary,
      margin: 0,
    },
    cardBody: {
      padding: "20px",
    },
    infoRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "10px 0",
      borderBottom: `1px solid ${theme.border}`,
    },
    infoLabel: {
      fontSize: "12px",
      color: theme.textMuted,
    },
    infoValue: {
      fontSize: "13px",
      fontWeight: 500,
      color: theme.textPrimary,
    },
    itemRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "12px 0",
      borderBottom: `1px solid ${theme.border}`,
    },
    itemName: {
      fontSize: "13px",
      fontWeight: 500,
      color: theme.textPrimary,
    },
    itemQty: {
      fontSize: "11px",
      color: theme.textMuted,
    },
    itemPrice: {
      fontSize: "13px",
      fontWeight: 600,
      color: theme.textPrimary,
    },
    totalRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "12px 0",
      fontWeight: 700,
      color: theme.textPrimary,
    },
    addressText: {
      fontSize: "13px",
      lineHeight: "1.5",
      color: theme.textSecondary,
    },
    noData: {
      textAlign: "center",
      padding: "40px",
      color: theme.textMuted,
    },
    timelineContainer: {
      maxHeight: "500px",
      overflowY: "auto",
      paddingRight: "8px",
    },
    cancelReasonBox: {
      marginTop: "16px",
      padding: "12px",
      background: `${theme.error || theme.danger}1a`,
      borderRadius: "8px",
      border: `1px solid ${theme.error || theme.danger}`,
    },
    cancelReasonText: {
      fontSize: "12px",
      color: theme.error || theme.danger,
    },
  };

  if (!order) {
    return (
      <div style={styles.container}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          <BackIcon /> Back to Orders
        </button>
        <div style={styles.noData}>No order data available</div>
      </div>
    );
  }

  // Get restaurant name
  const getRestaurantName = () => {
    return order?.restaurant ||     
           order?.restaurant_name || 
           order?.organisation_name || 
           order?.designation || 
           order?.organization_name || 
           order?.business_name || 
           order?.store_name || 
           order?.restro_name ||
           "Unknown Restaurant";
  };

  // Get driver name
  const getDriverName = () => {
    return order?.driver_name || 
           order?.rider_name || 
           order?.delivery_person_name ||
           "Not assigned";
  };

  // Get restaurant ID for navigation
  const getRestaurantId = () => {
    return order?.restaurant_id || 
           order?.designation_id || 
           order?.organisation_id || 
           order?.organization_id || 
           order?.business_id;
  };

  const restaurantName = getRestaurantName();
  const restaurantId = getRestaurantId();
  const driverName = getDriverName();

  // Handler for restaurant name click
  const handleRestaurantClick = () => {
    if (restaurantId) {
      navigate(`/restaurant/${restaurantId}`, { 
        state: { 
          restaurantId, 
          restaurantName,
          orderId: order.id 
        } 
      });
    } else {
      alert(`Restaurant: ${restaurantName}\nOrder ID: ${order.id}`);
    }
  };

  return (
    <div style={styles.container}>
      <button style={styles.backButton} onClick={() => navigate(-1)}>
        <BackIcon /> Back to Orders
      </button>

      <div style={styles.header}>
        <h1 style={styles.title}>Order Details</h1>
        <p style={styles.orderId}>Order #{order.id} • {new Date(order.created_at).toLocaleString()}</p>
      </div>

      <div style={styles.grid}>
        {/* Order Summary Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <PackageIcon style={{ color: theme.primary }} />
            <h3 style={styles.cardTitle}>Order Summary</h3>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Order Status</span>
              <span style={styles.infoValue}>
                <StatusBadge status={order.status} theme={theme} />
              </span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Restaurant Status</span>
              <span style={styles.infoValue}>{order.order_status_restro || "-"}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Driver Status</span>
              <span style={styles.infoValue}>{order.order_status_driver || "-"}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>User Status</span>
              <span style={styles.infoValue}>{order.order_status_user || "-"}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Driver Name</span>
              <span style={{ 
                ...styles.infoValue,
                color: driverName !== "Not assigned" ? theme.primary : theme.textMuted,
                fontWeight: driverName !== "Not assigned" ? 600 : 400
              }}>
                {driverName}
              </span>
            </div>
          </div>
        </div>

        {/* Customer Information Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <UserIcon style={{ color: theme.primary }} />
            <h3 style={styles.cardTitle}>Customer Information</h3>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Customer Name</span>
              <span style={styles.infoValue}>{order.receiver_name || "-"}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Phone Number</span>
              <span style={styles.infoValue}>{order.receiver_phone || "-"}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>User ID</span>
              <span style={styles.infoValue}>{order.userid || "-"}</span>
            </div>
          </div>
        </div>

        {/* Restaurant Information Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <StoreIcon style={{ color: theme.primary }} />
            <h3 style={styles.cardTitle}>Restaurant Information</h3>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Restaurant Name</span>
              <span 
                style={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: theme.primary,
                  cursor: "pointer",
                  textDecoration: "underline",
                  textDecorationColor: `${theme.primary}40`,
                  transition: "all 0.2s ease",
                  padding: "2px 4px",
                  borderRadius: "4px",
                }}
                onClick={handleRestaurantClick}
                onMouseEnter={(e) => {
                  e.target.style.color = theme.primary;
                  e.target.style.textDecorationColor = theme.primary;
                  e.target.style.backgroundColor = `${theme.primary}15`;
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = theme.primary;
                  e.target.style.textDecorationColor = `${theme.primary}40`;
                  e.target.style.backgroundColor = "transparent";
                }}
              >
                {restaurantName}
              </span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Designation ID</span>
              <span style={styles.infoValue}>{order.designation_id || "-"}</span>
            </div>
            {restaurantId && (
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Restaurant ID</span>
                <span style={styles.infoValue}>{restaurantId}</span>
              </div>
            )}
          </div>
        </div>

        {/* Payment Details Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <CreditCardIcon style={{ color: theme.primary }} />
            <h3 style={styles.cardTitle}>Payment Details</h3>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Payment Status</span>
              <span style={styles.infoValue}>
                <StatusBadge status={order.pay_status} theme={theme} />
              </span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Payment Method</span>
              <span style={styles.infoValue}>{order.pay_method || "-"}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Item Total</span>
              <span style={styles.infoValue}>{formatCurrency(itemTotal)}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Discount</span>
              <span style={styles.infoValue}>-{formatCurrency(discount)}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Tax</span>
              <span style={styles.infoValue}>{formatCurrency(tax)}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Delivery Charge</span>
              <span style={styles.infoValue}>{formatCurrency(deliveryCharge)}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Handling Charge</span>
              <span style={styles.infoValue}>{formatCurrency(handlingCharge)}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Platform Fee</span>
              <span style={styles.infoValue}>{formatCurrency(platformFee)}</span>
            </div>
            <div style={styles.totalRow}>
              <span>Final Amount</span>
              <span style={{ color: theme.success, fontSize: "18px" }}>{formatCurrency(finalAmount)}</span>
            </div>
          </div>
        </div>

        {/* Payment Tracking Card (COD only) - ✅ Now uses unified card style */}
        {order?.pay_method === "COD" && (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <MoneyIcon style={{ color: theme.primary }} />
              <h3 style={styles.cardTitle}>💰 Payment Tracking</h3>
            </div>

            <div style={styles.cardBody}>
              {/* Payment Received Via */}
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>💳 Payment Received Via</span>
                <strong style={{ color: theme.textPrimary }}>
                  {order.payment_received_mode === "CASH"
                    ? "💵 CASH"
                    : order.payment_received_mode === "COMPANY_QR"
                    ? "📱 COMPANY QR"
                    : "🌐 COMPANY ONLINE"}
                </strong>
              </div>

              {/* Customer Paid */}
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>👤 Customer Paid</span>
                <strong style={{ color: theme.success }}>
                  ✅ {order.pay_status}
                </strong>
              </div>

              {/* CASH */}
              {order.payment_received_mode === "CASH" && (
                <>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>🛵 Rider Collected Cash</span>
                    <strong
                      style={{
                        color: order.cash_collected_at ? theme.success : (theme.error || theme.danger),
                      }}
                    >
                      {order.cash_collected_at ? "✅ YES" : "❌ NO"}
                    </strong>
                  </div>

                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>🕒 Collected Time</span>
                    <strong style={{ color: theme.textPrimary }}>
                      {order.cash_collected_at
                        ? formatDate(order.cash_collected_at)
                        : "-"}
                    </strong>
                  </div>

                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>🏦 Rider Deposited Cash</span>
                    <strong
                      style={{
                        color:
                          Number(order.is_driver_deposit_cash) === 1
                            ? theme.success
                            : theme.warning,
                      }}
                    >
                      {Number(order.is_driver_deposit_cash) === 1
                        ? "✅ DEPOSITED"
                        : "⏳ PENDING"}
                    </strong>
                  </div>

                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>🛡️ Admin Verification</span>
                    <strong
                      style={{
                        color:
                          Number(order.payment_verified_by_admin) === 1
                            ? theme.success
                            : (theme.error || theme.danger),
                      }}
                    >
                      {Number(order.payment_verified_by_admin) === 1
                        ? "✅ VERIFIED"
                        : "❌ NOT VERIFIED"}
                    </strong>
                  </div>
                </>
              )}

              {/* COMPANY QR */}
              {order.payment_received_mode === "COMPANY_QR" && (
                <>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>🏦 Company Received Payment</span>
                    <strong style={{ color: theme.success }}>✅ YES</strong>
                  </div>

                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>💵 Rider Cash Collection</span>
                    <strong style={{ color: theme.primary }}>NOT REQUIRED</strong>
                  </div>

                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>🏦 Rider Deposit</span>
                    <strong style={{ color: theme.primary }}>NOT REQUIRED</strong>
                  </div>

                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>🛡️ Admin Verification</span>
                    <strong style={{ color: theme.primary }}>NOT REQUIRED</strong>
                  </div>
                </>
              )}

              {/* COMPANY ONLINE */}
              {order.payment_received_mode === "COMPANY_ONLINE" && (
                <>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>🏦 Company Received Payment</span>
                    <strong style={{ color: theme.success }}>✅ YES</strong>
                  </div>

                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>💵 Rider Cash Collection</span>
                    <strong style={{ color: theme.primary }}>NOT REQUIRED</strong>
                  </div>

                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>🏦 Rider Deposit</span>
                    <strong style={{ color: theme.primary }}>NOT REQUIRED</strong>
                  </div>

                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>🛡️ Admin Verification</span>
                    <strong style={{ color: theme.primary }}>NOT REQUIRED</strong>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Delivery Address Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <LocationIcon style={{ color: theme.primary }} />
            <h3 style={styles.cardTitle}>Delivery Address</h3>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Dropoff Address</span>
              <span style={styles.addressText}>{order.dropoff_address || "-"}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Pickup Address</span>
              <span style={styles.addressText}>{order.pickup_address || "-"}</span>
            </div>
          </div>
        </div>

        {/* Items Ordered Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <PackageIcon style={{ color: theme.primary }} />
            <h3 style={styles.cardTitle}>Items Ordered</h3>
          </div>
          <div style={styles.cardBody}>
            {items.length === 0 ? (
              <div style={styles.noData}>No items found</div>
            ) : (
              items.map((item, idx) => (
                <div key={idx} style={styles.itemRow}>
                  <div>
                    <div style={styles.itemName}>{item.name}</div>
                    <div style={styles.itemQty}>Quantity: {item.qty || 1}</div>
                    {item.variant_name && <div style={styles.itemQty}>Variant: {item.variant_name}</div>}
                  </div>
                  <div style={styles.itemPrice}>{formatCurrency(item.price * (item.qty || 1))}</div>
                </div>
              ))
            )}
            <div style={styles.totalRow}>
              <span>Total</span>
              <span>{formatCurrency(itemTotal)}</span>
            </div>
          </div>
        </div>

        {/* Financial Breakdown Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <MoneyIcon style={{ color: theme.primary }} />
            <h3 style={styles.cardTitle}>Financial Breakdown</h3>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Business Amount</span>
              <span style={styles.infoValue}>{formatCurrency(businessAmount)}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Driver Amount</span>
              <span style={styles.infoValue}>{formatCurrency(driverAmount)}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Platform Amount</span>
              <span style={styles.infoValue}>{formatCurrency(platformAmount)}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Delivery Charge</span>
              <span style={styles.infoValue}>{formatCurrency(deliveryCharge)}</span>
            </div>
          </div>
        </div>

        {/* Timeline Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <CalendarIcon style={{ color: theme.primary }} />
            <h3 style={styles.cardTitle}>Order Timeline</h3>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.timelineContainer}>
              {timelineEvents.map((event, idx) => (
                <VerticalTimelineEvent
                  key={idx}
                  label={event.label}
                  value={event.value || "—"}
                  isCompleted={event.isCompleted}
                  isLast={idx === timelineEvents.length - 1}
                  theme={theme}
                  suffix={event.suffix}
                />
              ))}
            </div>

            {/* Cancel Reason Section */}
            {cancelInfo && !timelineEvents.some(e => e.label === cancelInfo.type) && (
              <div style={styles.cancelReasonBox}>
                <span style={styles.cancelReasonText}>
                  <strong>{cancelInfo.type}:</strong> {cancelInfo.reason || "No reason provided"}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;